package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Compact result card for directory list "Nearby Results" (Stitch 19bc25c5): cover image on top,
 * then name with category + saffron star/reviews line, an optional open/closed status column at the
 * end, and a footer row with the location and a tonal "Details" button. All labels localized by the
 * caller; [ratingLine] is the formatted "4.6 (85 reviews)" text.
 */
@Composable
fun NearbyBusinessCard(
    name: String,
    category: String,
    ratingLine: String,
    locationLabel: String,
    detailsLabel: String,
    onDetails: () -> Unit,
    modifier: Modifier = Modifier,
    statusLabel: String? = null,
    statusIsOpen: Boolean = true,
    statusDetail: String? = null,
    onClick: (() -> Unit)? = null,
    coverIcon: String = "storefront",
    coverImageRes: org.jetbrains.compose.resources.DrawableResource? = null,
    image: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(AppTheme.sizes.resultImageHeight)
                    .clip(AppTheme.shapes.card),
            ) {
                if (image != null) image() else BusinessCover(iconName = coverIcon, placeholderImageRes = coverImageRes)
            }
            Column(modifier = Modifier.padding(AppTheme.spacing.lg)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    ) {
                        Text(
                            text = name,
                            style = AppTheme.typography.headlineMd,
                            color = AppTheme.colors.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                        ) {
                            Text(
                                text = category,
                                style = AppTheme.typography.bodySm,
                                color = AppTheme.colors.onSurfaceVariant,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.weight(1f, fill = false),
                            )
                            AppIcon(name = "star", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.secondary, filled = true)
                            Text(
                                text = ratingLine,
                                style = AppTheme.typography.bodySm,
                                color = AppTheme.colors.secondary,
                            )
                        }
                    }
                    if (statusLabel != null) {
                        Column(horizontalAlignment = Alignment.End) {
                            Text(
                                text = statusLabel,
                                style = AppTheme.typography.labelSm,
                                color = if (statusIsOpen) AppTheme.colors.success else AppTheme.colors.alert,
                                textAlign = TextAlign.End,
                            )
                            if (statusDetail != null) {
                                Text(
                                    text = statusDetail,
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onSurfaceVariant,
                                    textAlign = TextAlign.End,
                                )
                            }
                        }
                    }
                }
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.md),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        modifier = Modifier.weight(1f),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    ) {
                        AppIcon(name = "location_on", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
                        Text(
                            text = locationLabel,
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                    AppButton(
                        text = detailsLabel,
                        onClick = onDetails,
                        variant = AppButtonVariant.Tonal,
                    )
                }
            }
        }
    }
}
